import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixSocialGroupsV2MembershipQuestion from './social-groups-v2-membership-question-membership-questions.http.js';

/**
 * Question asked to members when joining a group.
 * Only group admins can create or update membership questions
 * and can set whether it is required to answer membership questions.
 */
export interface MembershipQuestion {
  /**
   * Question ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Whether a member must answer this question when joining the group. */
  required?: boolean;
  /**
   * Question text.
   * @minLength 1
   * @maxLength 300
   */
  text?: string;
}

export interface ListMembershipQuestionsRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
}

export interface ListMembershipQuestionsResponse {
  /** Retrieved membership questions. */
  questions?: MembershipQuestion[];
}

export interface CreateOrReplaceAllMembershipQuestionsRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * New membership questions.
   * @maxSize 50
   */
  questions?: MembershipQuestion[];
}

export interface CreateOrReplaceAllMembershipQuestionsResponse {
  /** Membership questions. */
  questions?: MembershipQuestion[];
}

export interface ListAnswersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Member IDs. If no member ID is provided, answers for all members will be returned. See the Members API for more details.
   * @format GUID
   * @maxSize 500
   */
  memberIds?: string[];
  paging?: Paging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListAnswersResponse {
  /** Answers to the membership questions, grouped by member. */
  memberAnswers?: MemberMembershipQuestionAnswers[];
  /** Membership questions by question ID. Includes only questions that have been answered by the specified members. */
  questions?: Record<string, MembershipQuestion>;
  metadata?: PagingMetadata;
}

export interface MemberMembershipQuestionAnswers {
  /**
   * Member ID. See the Members API for more details.
   * @format GUID
   */
  memberId?: string;
  /** Answers to the membership question. */
  answers?: MembershipQuestionAnswer[];
}

/** Answer to a membership question. */
export interface MembershipQuestionAnswer {
  /**
   * Question ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Answer text.
   * @maxLength 300
   */
  text?: string | null;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface ListAnswerCountsRequest {
  /**
   * @format GUID
   * @maxSize 500
   */
  memberIds?: string[];
}

export interface ListAnswerCountsResponse {
  memberAnswerCounts?: MemberAnswerCount[];
}

export interface MemberAnswerCount {
  /** @format GUID */
  memberId?: string;
  /** @format GUID */
  groupId?: string;
  count?: number;
}

/**
 * Retrieves the membership questions for a group.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
 * @param groupId - Group ID.
 * @public
 * @requiredField groupId
 * @fqn wix.social.groups.api.v2.MembershipQuestionsService.ListMembershipQuestions
 */
export async function listMembershipQuestions(
  groupId: string
): Promise<
  NonNullablePaths<
    ListMembershipQuestionsResponse,
    `questions` | `questions.${number}.required` | `questions.${number}.text`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ groupId: groupId });

  const reqOpts =
    ambassadorWixSocialGroupsV2MembershipQuestion.listMembershipQuestions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { groupId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['groupId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates membership questions if none have been set up. Otherwise, replaces all existing questions.
 *
 * Providing an empty array means that members won't have to answer any question when joining the group.
 * Only admins can create or replace membership questions.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
 * @param groupId - Group ID.
 * @public
 * @requiredField groupId
 * @fqn wix.social.groups.api.v2.MembershipQuestionsService.CreateOrReplaceAllMembershipQuestions
 */
export async function createOrReplaceAllMembershipQuestions(
  groupId: string,
  options?: CreateOrReplaceAllMembershipQuestionsOptions
): Promise<
  NonNullablePaths<
    CreateOrReplaceAllMembershipQuestionsResponse,
    `questions` | `questions.${number}.required` | `questions.${number}.text`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupId: groupId,
    questions: options?.questions,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2MembershipQuestion.createOrReplaceAllMembershipQuestions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          groupId: '$[0]',
          questions: '$[1].questions',
        },
        singleArgumentUnchanged: false,
      },
      ['groupId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateOrReplaceAllMembershipQuestionsOptions {
  /**
   * New membership questions.
   * @maxSize 50
   */
  questions?: MembershipQuestion[];
}

/**
 * Retrieves the answers to the membership questions, given the provided filters.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
 * @param groupId - Group ID.
 * @public
 * @requiredField groupId
 * @fqn wix.social.groups.api.v2.MembershipQuestionsService.ListAnswers
 */
export async function listAnswers(
  groupId: string,
  options?: ListAnswersOptions
): Promise<
  NonNullablePaths<
    ListAnswersResponse,
    `memberAnswers` | `memberAnswers.${number}.memberId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupId: groupId,
    memberIds: options?.memberIds,
    paging: options?.paging,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2MembershipQuestion.listAnswers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          groupId: '$[0]',
          memberIds: '$[1].memberIds',
          paging: '$[1].paging',
        },
        singleArgumentUnchanged: false,
      },
      ['groupId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListAnswersOptions {
  /**
   * Member IDs. If no member ID is provided, answers for all members will be returned. See the Members API for more details.
   * @format GUID
   * @maxSize 500
   */
  memberIds?: string[];
  paging?: Paging;
}
